import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const BookingDetailsModal = ({ isOpen, onClose, booking, onPayNow, onCancel }) => {
  if (!isOpen || !booking) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'gcash':
        return 'GCash';
      case 'credit_card':
        return 'Credit Card';
      case 'pay_at_hotel':
        return 'Pay at Hotel';
      case 'bank_transfer':
        return 'Bank Transfer';
      default:
        return 'Pending';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-success text-success-foreground';
      case 'pending':
        return 'bg-warning text-warning-foreground';
      case 'failed':
        return 'bg-error text-error-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Booking Details</h2>
            <p className="text-sm text-muted-foreground mt-1">Confirmation #{booking?.confirmationNumber}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Booking Image & Status */}
          <div className="relative rounded-xl overflow-hidden h-64">
            <Image
              src={booking?.image}
              alt={booking?.imageAlt}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 flex gap-2">
              <span className={`px-4 py-2 text-sm font-semibold rounded-full ${getPaymentStatusColor(booking?.paymentStatus)}`}>
                {booking?.paymentStatus?.charAt(0)?.toUpperCase() + booking?.paymentStatus?.slice(1)}
              </span>
            </div>
          </div>

          {/* Booking Information */}
          <div>
            <h3 className="text-xl font-bold text-foreground mb-1">{booking?.name}</h3>
            <div className="flex items-center gap-2 text-muted-foreground mb-3">
              <Icon name="MapPin" size={16} />
              <span>{booking?.location}</span>
            </div>
            <p className="text-muted-foreground">{booking?.description}</p>
          </div>

          {/* Booking Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Calendar" size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Check-in Date</p>
                <p className="text-sm font-semibold text-foreground">{formatDate(booking?.checkIn)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                <Icon name="Calendar" size={20} className="text-secondary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Check-out Date</p>
                <p className="text-sm font-semibold text-foreground">{formatDate(booking?.checkOut)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name="Users" size={20} className="text-accent" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Number of Guests</p>
                <p className="text-sm font-semibold text-foreground">{booking?.guests} guests</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <Icon name="Hash" size={20} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Booking ID</p>
                <p className="text-sm font-semibold text-foreground">{booking?.id}</p>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="border border-border rounded-xl overflow-hidden">
            <div className="p-4 bg-muted/50 border-b border-border">
              <h4 className="font-semibold text-foreground">Payment Information</h4>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Payment Method</span>
                <span className="text-sm font-semibold text-foreground">{getPaymentMethodLabel(booking?.paymentMethod)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Payment Status</span>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(booking?.paymentStatus)}`}>
                  {booking?.paymentStatus?.charAt(0)?.toUpperCase() + booking?.paymentStatus?.slice(1)}
                </span>
              </div>
              {booking?.dueDate && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Payment Due Date</span>
                  <span className="text-sm font-semibold text-warning">{formatDate(booking?.dueDate)}</span>
                </div>
              )}
              <div className="pt-3 border-t border-border flex justify-between items-center">
                <span className="text-base font-semibold text-foreground">Total Amount</span>
                <span className="text-2xl font-bold text-primary">
                  ₱{booking?.amount?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          {/* Cancellation Policy */}
          <div className="border border-border rounded-xl overflow-hidden">
            <div className="p-4 bg-muted/50 border-b border-border">
              <h4 className="font-semibold text-foreground flex items-center gap-2">
                <Icon name="AlertCircle" size={18} />
                Cancellation Policy
              </h4>
            </div>
            <div className="p-4">
              <p className="text-sm text-muted-foreground leading-relaxed">{booking?.cancellationPolicy}</p>
            </div>
          </div>

          {/* Refund Policy */}
          <div className="border border-border rounded-xl overflow-hidden">
            <div className="p-4 bg-muted/50 border-b border-border">
              <h4 className="font-semibold text-foreground flex items-center gap-2">
                <Icon name="RefreshCw" size={18} />
                Refund Policy
              </h4>
            </div>
            <div className="p-4">
              <p className="text-sm text-muted-foreground leading-relaxed">{booking?.refundPolicy}</p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="border border-border rounded-xl overflow-hidden">
            <div className="p-4 bg-muted/50 border-b border-border">
              <h4 className="font-semibold text-foreground flex items-center gap-2">
                <Icon name="Phone" size={18} />
                Contact Information
              </h4>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Icon name="Mail" size={16} className="text-muted-foreground" />
                <a href={`mailto:${booking?.contactEmail}`} className="text-sm text-primary hover:underline">
                  {booking?.contactEmail}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="Phone" size={16} className="text-muted-foreground" />
                <a href={`tel:${booking?.contactPhone}`} className="text-sm text-primary hover:underline">
                  {booking?.contactPhone}
                </a>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              size="lg"
              fullWidth
              onClick={onClose}
            >
              Close
            </Button>
            {(booking?.paymentStatus === 'pending' || booking?.paymentStatus === 'failed') && (
              <Button
                variant="success"
                size="lg"
                fullWidth
                onClick={() => {
                  onClose();
                  onPayNow(booking?.id);
                }}
                iconName="CreditCard"
                iconPosition="left"
              >
                Pay Now
              </Button>
            )}
            {booking?.paymentStatus === 'paid' && (
              <Button
                variant="default"
                size="lg"
                fullWidth
                iconName="Download"
                iconPosition="left"
              >
                Download Receipt
              </Button>
            )}
            {booking?.bookingStatus === 'confirmed' && (
              <Button
                variant="destructive"
                size="lg"
                fullWidth
                onClick={() => {
                  onClose();
                  onCancel(booking?.id);
                }}
                iconName="XCircle"
                iconPosition="left"
              >
                Cancel Booking
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailsModal;